import { networkAtom } from "@/atoms/settings"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import { useRenecConnect } from "@/libs/wallet-provider/renec-provider/useRenecConnect"
import { useSolanaConnect } from "@/libs/wallet-provider/solana-provider/useSolanaConnect"
import { useCheckClient } from "@/providers/client"
import { shortenAddress } from "@/utils/helpers"
import { ConstantsHelper } from "@renec-foundation/relend-adapter"
import { WalletIcon } from "@renec-foundation/wallet-adapter-react"
import { useAtomValue } from "jotai"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button, type ButtonProps } from "./ConnectWalletButton"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import { Box } from "@mui/material"

type ButtonConnectProps = { showDropDownIcon?: boolean } & ButtonProps

const getLabels = (trans: any): { [key in string]: string } =>
  ({
    "change-wallet": trans.wallet.change_wallet,
    connecting: trans.wallet.connecting,
    "copy-address": trans.wallet.copy_address,
    copied: trans.wallet.copied,
    disconnect: trans.wallet.disconnect,
    "renec-has-wallet": trans.wallet.connect_wallet,
    "solana-has-wallet": trans.wallet.connect_wallet,
    "no-wallet": trans.wallet.select_wallet,
  }) as const

const DEMON_WALLET_ICON =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAVOSURBVHgB1ZpNbBtFFMffOIH0EJuKBsWyOZhD8eZCOOBQoRzyoRbooXFVvtTUyK4qURJBLxUJBASVkqriVFKlfBwaq+YGrZITvcQJEpWQc2ly6RoO+ICjIOVQeTmlqrfzH8eW63g9M05duz9p7f3e9968efPezjKS4H3RGLDtByNkswFiFCCb9lMjYXSP/9whyt9hrG1x819zpfbpDnj9RtS27a+J7AA1lwxj9oXN7N/xagd3KeANGAH7vj1Ptj1ArQRjK+wZFtvMmJlHdpdvCOG37eUWsLoTGfasa7BcCVdxpZnCh0Ih1VO5jPllyFrcUVJAuE0ThA+HR+g5j1vnkoKL7yAUQIdtls+Pj42RNlxWr/9gFKvthW1Em8dHj2GQwZdystksmek05XK50j6c4/f7qNOt1QIC22aQOd4u4nw+H6A94PF4KDxyjIaGh8gIGnzbWSDTTFNqdZUSiZ/J7/OJfQVlF0v3KleyBgHIzrr9By/zQeoc1QEeFomMUuRUpKbQTkAZwwiSZVn0+qE3eGv4xfbSUlLtBsz+rq3T88IEXw2QJrBafP4aDXOrd3R0UD10dXWJf3E9D+jDQ0O0tbUlFFOD7eN9wH6VNEHkmJyYqMvqTqAz53IW5XhrFN1JgUC7bm4zPj7GH/YxNQIYBC6kwX6Xztlwl0YIj06dSq2K9T4+qKFvqdKueiI6GNxGKgwXJJlMCj9G6ARublm/zy8MAPerBGH38JG3RCSbnJzgQWGU5q5+Tyqwbt/LtsqJF2emqz68SHZjg6a++FJYsxaI+/D3yntBcUQjKAmgUNEAtFcF0Kzx+DXH47B2NBYTnVCVDyOnhLWdgCGi0dMko63TfeAb2UkzF6eFC1UDlj95clRLeLC2vk6InX191RM5PM/HF7hjLaSdGDfqc8gWEfKi0Zi28EWWJMId5272+WTtfidVIBR6zfFY4nqC++kG1Us4fGwn7jsT4a5WC2kUwujoxMKi8oBTlUuXvhULOjZaGXVBeeeGcgsLtZ8h7cQ3b/yyK7MEiBrRmLyT6YKB7MqVWbK4W6oEBmkL+HYyxkqQGjcCRLQTJ95R7lfSPuA0KlpqKW9d6AQFrVSiFZEq4BRl0PFaAakCllXdVUKhPmoFpAoUs8RKiqGv2UgVSCaXHY+NjTemLtBBqsBd03QsstECx2tkqDIQ4ca5EW7e+FUki+E67iVN5ra3t3nNus8x6cL+23/cFrWsDhB+dvYyN0BY1MbIuQr1Qlik1ap1sVI2avJWeP+9d6sW79h39OjbWsU4hP3pxx+ot/eVXcdQVkIR63+L1tbWpfdSUgCtgKW/v7/qcSiBh0IwWA8pdjVg9TNnTtPM9HTNMIzrz5//jFRQrsgA/FQl8mDsMNMmmXdNse3mgiPHUY1ah4+8qZzlaikAq8Xj86U3ao0A2en1REL5fK1UAlZBAePkIntlbu6qlvDAVZiTUqeohKx41wF5/yefnlN+E1HGPf5q8cAHfMWrc5VVKjSY8O16Xy0CjPQfnT2rFHEqYYz9yRV4voevHqI6WOWt8NutW+Rxe0RH9Wi8JofgU1NfCatbVn01NX+5u8h2Xq8v02MA5WeID2xGMEhGj1FSCC6CCiuVSlGaF0IoRet9EVAOc7kGxSRfty/4TwtP7DmR+W/jr5dEFOK+dIGeMjB3LP6LO7r9weWWmxt2gBt8ZTObHsR6aRxgD1iM/2ao9cmQkLXAoxPdXj5X7Grxie48n+jerDLRDXCA5dkgpvWpxYDbVAov9jtd8NR+7FHJzuc2YUauXhvzaU/kcxvu52T/zljbguxzm4eEJSgbzT2RUAAAAABJRU5ErkJggg=="

const ButtonConnect = ({ showDropDownIcon = true, listMenuItems, children, ...props }: ButtonConnectProps) => {
  const ref = useRef<HTMLUListElement>(null)
  const { solanaWallet, solanaPubkeyBs58 } = useSolanaConnect()
  const { renecWallet, renecPubkeyBs58 } = useRenecConnect()
  const trans = useTrans()
  const { buttonState, connectWallet, openChangeWalletModal, disconnectWallet } = useWalletConnect()
  const [menuOpen, setMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const network = useAtomValue(networkAtom)
  const { isClient } = useCheckClient()

  const isRenecChain = ConstantsHelper.isRenecChain(network)
  const isSolanaChain = ConstantsHelper.isSolanaChain(network)

  const address = useMemo(() => {
    if (isSolanaChain) {
      return solanaPubkeyBs58
    }
    if (isRenecChain) {
      return renecPubkeyBs58
    }
    return undefined
  }, [isSolanaChain, isRenecChain, solanaPubkeyBs58, renecPubkeyBs58])

  const wallet = useMemo(() => {
    if (isSolanaChain) {
      return solanaWallet
    }
    if (isRenecChain) {
      return renecWallet
    }
    return renecWallet
  }, [isSolanaChain, isRenecChain, renecWallet, solanaWallet])

  const isConnected = useMemo(
    () => (renecPubkeyBs58 && isRenecChain) || (solanaPubkeyBs58 && isSolanaChain),
    [isRenecChain, isSolanaChain, renecPubkeyBs58, solanaPubkeyBs58],
  )

  const content = useMemo(() => {
    if (children) {
      return children
    } else if (isConnected) {
      return shortenAddress(address)
    } else if (
      buttonState === "connecting" ||
      buttonState === "renec-has-wallet" ||
      buttonState === "solana-has-wallet"
    ) {
      return getLabels(trans)[buttonState]
    } else {
      return getLabels(trans)["no-wallet"]
    }
  }, [address, buttonState, children, trans, isConnected])

  const copyAddress = useCallback(async () => {
    if (address) {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 400)
    }
  }, [address])

  const openDropdown = useCallback(() => {
    setMenuOpen(true)
  }, [])

  const closeDropdown = useCallback(() => {
    setMenuOpen(false)
  }, [])

  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const node = ref.current

      // Do nothing if clicking dropdown or its descendants
      if (!node || node.contains(event.target as Node)) return

      closeDropdown()
    }

    document.addEventListener("mousedown", listener)
    document.addEventListener("touchstart", listener)

    return () => {
      document.removeEventListener("mousedown", listener)
      document.removeEventListener("touchstart", listener)
    }
  }, [ref, closeDropdown])

  return isClient ? (
    <div className='wallet-adapter-button-wrap' data-testid='custom-wallet-adapter-button-wrap'>
      <Button
        aria-expanded={menuOpen}
        className={`rounded font-bold py-[10px] px-4 leading-[26px] sm:leading-5 ${isConnected ? "bg-white text-primary border border-solid border-primary" : "bg-primary border border-solid border-primary text-white hover:bg-primary/80"}`}
        style={{ pointerEvents: menuOpen ? "none" : "auto", ...props.style }}
        onClick={() => {
          switch (buttonState) {
            case "no-wallet":
              openChangeWalletModal()
              break
            case "renec-has-wallet":
            case "solana-has-wallet":
              if (connectWallet) {
                connectWallet(network)
              }
              break
            case "connected":
              openDropdown()
              break
          }
        }}
        beginIcon={
          wallet ? (
            isRenecChain ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={DEMON_WALLET_ICON} alt='Demon icon' style={{ width: "auto", height: "auto" }} />
            ) : (
              <WalletIcon wallet={wallet} />
            )
          ) : undefined
        }
        {...props}
      >
        <span className='wallet-adapter-button-content'>{content}</span>
        {showDropDownIcon && buttonState !== "connected" ? (
          <Box
            onClick={(event) => {
              event.stopPropagation()
              openChangeWalletModal()
            }}
          >
            <ArrowDropDownIcon />
          </Box>
        ) : null}
      </Button>
      <ul
        aria-label='dropdown-list'
        className={`wallet-adapter-dropdown-list ${menuOpen && "wallet-adapter-dropdown-list-active"}`}
        ref={ref}
        role='menu'
      >
        <li onClick={copyAddress} role='menuitem'>
          {copied ? trans.wallet.copied : trans.wallet.copy_address}
        </li>
        <li
          className='wallet-adapter-dropdown-list-item'
          onClick={() => {
            openChangeWalletModal()
            closeDropdown()
          }}
          role='menuitem'
        >
          {getLabels(trans)["change-wallet"]}
        </li>
        {listMenuItems}
        <li
          onClick={() => {
            disconnectWallet()
            closeDropdown()
          }}
          role='menuitem'
        >
          {trans.wallet.disconnect}
        </li>
      </ul>
    </div>
  ) : null
}

export default ButtonConnect
